import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconEdit, IconTrash, IconFile, IconDownload } from '@tabler/icons';
import { formatDistanceToNow, parseJSON, format } from 'date-fns';
import { generateHTML } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import {
  CaseNote as CaseNoteType,
  CaseNoteAttachment,
} from '../../../types/support.types';
import CaseMessageComposer from './CaseMessageComposer';

const extensions = [StarterKit, Underline, Link];

function renderNoteBody(note: CaseNoteType): string {
  if (note.body && typeof note.body === 'object') {
    try {
      return generateHTML(note.body, extensions);
    } catch {
      return note.body_text || '';
    }
  }
  if (typeof note.body === 'string') {
    return note.body;
  }
  return note.body_text || '';
}

function formatFileSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageAttachment(att: CaseNoteAttachment): boolean {
  return !!att.content_type && att.content_type.startsWith('image/');
}

const ImageAttachmentPreview: React.FC<{
  attachment: CaseNoteAttachment;
  getUrl: () => Promise<string>;
  onDownload?: () => void;
}> = ({ attachment, getUrl, onDownload }) => {
  const [url, setUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    getUrl()
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [getUrl]);

  if (error) return null;

  return (
    <Box
      onClick={onDownload}
      sx={{
        mt: 0.5,
        cursor: onDownload ? 'pointer' : 'default',
        borderRadius: 1,
        overflow: 'hidden',
        maxWidth: 240,
        bgcolor: 'action.hover',
      }}
    >
      {url ? (
        <img
          src={url}
          alt={attachment.file_name}
          style={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: 200,
            objectFit: 'contain',
            borderRadius: 4,
          }}
        />
      ) : (
        <Skeleton variant="rectangular" width={200} height={120} />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
        }}
      >
        <Typography variant="caption" noWrap sx={{ flex: 1 }}>
          {attachment.file_name}
        </Typography>
        {attachment.size_bytes != null && (
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(attachment.size_bytes)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

interface CaseNoteProps {
  note: CaseNoteType;
  onUpdate: (noteId: string, body: Record<string, any>) => void;
  onDelete: (noteId: string) => void;
  isUpdating: boolean;
  onDownloadAttachment?: (attachment: CaseNoteAttachment) => void;
  getAttachmentUrl?: (attachment: CaseNoteAttachment) => Promise<string>;
}

const CaseNote: React.FC<CaseNoteProps> = ({
  note,
  onUpdate,
  onDelete,
  isUpdating,
  onDownloadAttachment,
  getAttachmentUrl,
}) => {
  const [editing, setEditing] = React.useState(false);

  const handleSave = (body: Record<string, any>) => {
    onUpdate(note.id, body);
    setEditing(false);
  };

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'rgba(255, 193, 7, 0.04)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" fontWeight={600}>
            {note.author?.display_name || note.author?.email || 'Unknown'}
          </Typography>
          <Tooltip
            title={format(parseJSON(note.created_at), 'PPpp')}
            placement="top"
          >
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(parseJSON(note.created_at), {
                addSuffix: true,
              })}
            </Typography>
          </Tooltip>
        </Box>
        {!editing && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={() => setEditing(true)}>
              <IconEdit size={14} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(note.id)}
              color="error"
            >
              <IconTrash size={14} />
            </IconButton>
          </Box>
        )}
      </Box>

      {editing ? (
        <Box>
          <CaseMessageComposer
            onSend={handleSave}
            isPending={isUpdating}
            placeholder="Edit note..."
            initialContent={note.body || undefined}
          />
          <Button size="small" onClick={() => setEditing(false)} sx={{ mt: 1 }}>
            Cancel
          </Button>
        </Box>
      ) : (
        <>
          <Box
            sx={{ '& p': { m: 0 } }}
            dangerouslySetInnerHTML={{ __html: renderNoteBody(note) }}
          />
          {(note.attachments?.length ?? 0) > 0 && (
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              {note.attachments!.map((att) => {
                if (isImageAttachment(att) && getAttachmentUrl) {
                  return (
                    <ImageAttachmentPreview
                      key={att.id}
                      attachment={att}
                      getUrl={() => getAttachmentUrl(att)}
                      onDownload={() => onDownloadAttachment?.(att)}
                    />
                  );
                }
                return (
                  <Box
                    key={att.id}
                    onClick={() => onDownloadAttachment?.(att)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      cursor: onDownloadAttachment ? 'pointer' : 'default',
                      '&:hover': onDownloadAttachment
                        ? { bgcolor: 'action.selected' }
                        : {},
                    }}
                  >
                    <IconFile size={14} />
                    <Typography variant="caption" sx={{ flex: 1 }}>
                      {att.file_name}
                    </Typography>
                    {att.size_bytes != null && (
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(att.size_bytes)}
                      </Typography>
                    )}
                    {onDownloadAttachment && <IconDownload size={14} />}
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CaseNote;
